import { getActivityById } from "./activityCatalog";
import type { DrawingActivity } from "./activityTypes";
import { clampGuideStep } from "./GuideSessionController";

export type ActivityRouteResolution =
  | { kind: "none" }
  | { kind: "invalid"; notice: string }
  | {
      kind: "activity";
      activity: DrawingActivity;
      step: number;
      stepWasClamped: boolean;
      canonicalSearch: string;
    };

function asSearchParams(input: string | URLSearchParams): URLSearchParams {
  if (input instanceof URLSearchParams) return new URLSearchParams(input);
  return new URLSearchParams(input.startsWith("?") ? input.slice(1) : input);
}

function parseStep(value: string | null): number {
  if (value === null || !/^-?\d+$/.test(value)) return 1;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : 1;
}

export function serializeActivityRoute(activity: DrawingActivity, step = 1): string {
  const search = new URLSearchParams();
  search.set("activity", activity.id);
  if (activity.type === "guided") {
    search.set("step", String(clampGuideStep(step, activity.steps.length)));
  }
  return search.toString();
}

/** Removes only ephemeral activity state and preserves every unrelated query value. */
export function withoutActivityRoute(input: string | URLSearchParams): URLSearchParams {
  const search = asSearchParams(input);
  search.delete("activity");
  search.delete("step");
  return search;
}

export function parseActivityRoute(input: string | URLSearchParams): ActivityRouteResolution {
  const search = asSearchParams(input);
  const activityId = search.get("activity");
  if (!activityId) {
    return search.has("step")
      ? {
          kind: "invalid",
          notice: "That activity link is incomplete, so this opened as a normal drawing.",
        }
      : { kind: "none" };
  }
  const activity = getActivityById(activityId);
  if (!activity) {
    return {
      kind: "invalid",
      notice: "We couldn't find that activity, so this opened as a normal drawing.",
    };
  }
  if (activity.type === "challenge") {
    return {
      kind: "activity",
      activity,
      step: 1,
      stepWasClamped: false,
      canonicalSearch: serializeActivityRoute(activity),
    };
  }
  const rawStep = search.get("step");
  const parsedStep = parseStep(rawStep);
  const step = clampGuideStep(parsedStep, activity.steps.length);
  const canonicalSearch = serializeActivityRoute(activity, step);
  return {
    kind: "activity",
    activity,
    step,
    stepWasClamped: rawStep !== String(step),
    canonicalSearch,
  };
}
